import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, Shield, Users, UserRound, Tags, FileSpreadsheet, SlidersHorizontal, Gavel, ScrollText,
  FileText, Users2, History, BarChart3, Bell, ClipboardList, Settings, LogOut, Menu, Monitor, X,
} from "lucide-react";
import { Logo } from "@/components/opl/Common";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";

export const MODULES = [
  ["", "Dashboard", LayoutDashboard, ["view_reports", "manage_players", "manage_teams"]],
  ["clubs", "Clubs & Branches", Building2, ["manage_clubs"]],
  ["teams", "Teams", Shield, ["manage_teams"]],
  ["users", "Owners & Users", Users, ["manage_users"]],
  ["players", "Player Database", UserRound, ["manage_players"]],
  ["categories", "Player Categories", Tags, ["manage_config"]],
  ["import", "Import & Export", FileSpreadsheet, ["manage_players"]],
  ["config", "Auction Configuration", SlidersHorizontal, ["manage_config"]],
  ["live", "Live Auction Control", Gavel, ["run_auction"]],
  ["rules", "Auction Rules", ScrollText, ["manage_rules"]],
  ["rulebook", "Rulebook Generator", FileText, ["manage_rules", "publish_rules"]],
  ["squads", "Team Squads", Users2, ["view_reports"]],
  ["bids", "Bid History", History, ["view_reports"]],
  ["reports", "Reports & Analytics", BarChart3, ["view_reports"]],
  ["notifications", "Notifications", Bell, ["manage_notifications"]],
  ["audit", "Audit Logs", ClipboardList, ["view_audit"]],
  ["settings", "Settings & Permissions", Settings, ["manage_settings"]],
];

function Nav({ onNav }) {
  const { can } = useAuth();
  return (
    <nav className="flex flex-col gap-0.5 p-3">
      {MODULES.filter((m) => can(...m[3])).map(([path, label, Icon]) => (
        <NavLink key={path} to={`/admin${path ? "/" + path : ""}`} end onClick={onNav} data-testid={`admin-sidebar-${path || "dashboard"}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[#E5B842]/10 text-[#F5D061] border-l-2 border-[#E5B842]" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
          <Icon className="h-4 w-4 shrink-0" />{label}
        </NavLink>
      ))}
      <Link to="/live" target="_blank" className="flex items-center gap-3 px-3 py-2 mt-2 rounded-md text-sm text-red-300 hover:bg-red-500/10" data-testid="admin-sidebar-led">
        <Monitor className="h-4 w-4" />LED Display ↗
      </Link>
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const out = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/5 bg-[#070F1C] sticky top-0 h-screen">
        <div className="p-5 border-b border-white/5"><Link to="/"><Logo /></Link></div>
        <div className="flex-1 overflow-y-auto"><Nav /></div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#070F1C] overflow-y-auto">
            <div className="p-5 flex justify-between border-b border-white/5"><Logo /><button onClick={() => setOpen(false)}><X /></button></div>
            <Nav onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="h-14 border-b border-white/5 flex items-center justify-between px-4 lg:px-8 bg-[#050B14]/80 backdrop-blur sticky top-0 z-30">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu /></button>
          <div className="text-xs text-slate-500 hidden sm:block">OPL Season 6 · Control Center</div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#E5B842]" data-testid="current-user-role">{ROLE_LABEL[user?.role]}</div>
            </div>
            <button onClick={out} className="p-2 rounded-md hover:bg-white/5 text-slate-400" data-testid="logout-btn" title="Log out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-[1500px]"><Outlet /></main>
      </div>
    </div>
  );
}
